import React, {useCallback, useContext, useMemo} from "react";
import OwlCarousel from "react-owl-carousel";
import classnames from "classnames";
import LazyImage from "../../../../../../../../components/app-specific/lazy-image";
import {UserHomepageControllerContext} from "../../../../../../index";
import {UserHomepageEventEmitter} from "../../../../../../models";
import {ReactComponent as Prev} from "../../../../../../../../../assets/images/web-homepage/slide-show-prev.svg";
import {ReactComponent as Next} from "../../../../../../../../../assets/images/web-homepage/slide-show-next.svg";
import {useBizFileUrlsResolver} from "../../../../../../../../hooks/use-biz-file-urls-resolver";
import useImageCarouselOwl from "../../../../../../../../hooks/use-image-carousel-owl";

const PHONE_IMAGE_CAROUSEL_MARGIN = 6;

const UserPhoneHomepageImageCarousel = ({component, tabId}) => {
    const controller = useContext(UserHomepageControllerContext);
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);
    const images = useMemo(() => component?.content?.images ?? [], [component]);
    const imageWidth = component?.content?.imageWidth;
    const imageHeight = component?.content?.imageHeight;

    const {
        sliderRef,
        carouselRef,
        containerIdRef,
        isScrollable,
        hasNext,
        hasPrev,
        slideStyle,
        imageStyle,
        owlClassName,
        owlProps,
        changeSlide,
        onInitialized,
        onChanged,
    } = useImageCarouselOwl({
        images,
        imageWidth,
        imageHeight,
        margin: PHONE_IMAGE_CAROUSEL_MARGIN,
        reactKey: true,
    });

    const onImageClicked = (image) => {
        controller.clickContent(tabId, component.id, image.id, UserHomepageEventEmitter.view);
    }

    const getImageSrc = useCallback((image) =>
            bizFileUrls.getFilePreviewUrl(image?.fileGuid, image?.fileExtension) ?? image?.mockPreviewUrl,
        [bizFileUrls]);

    const owlCarousel = useMemo(() => (
        <OwlCarousel
            {...owlProps}
            ref={carouselRef}
            onInitialized={onInitialized}
            onChanged={onChanged}
            id={containerIdRef.current}
            className={owlClassName}>
            {images.map((image) => (
                <div
                    key={image.id}
                    id={image.phoneViewElementId}
                    className={classnames(
                        'user-phone-homepage-image-carousel__slide',
                        {'clickable': !!image?.tags?.length},
                    )}
                    style={slideStyle}
                    onClick={() => onImageClicked(image)}>
                    <div
                        className={'image'}
                        style={imageStyle}>
                        <LazyImage
                            src={getImageSrc(image)}
                            alt={image?.tags?.[0]?.name ?? ''}
                            width={imageStyle.width}
                            height={imageHeight}
                            className={'user-phone-homepage-image-carousel__img'}
                        />
                    </div>
                </div>
            ))}
        </OwlCarousel>
    ), [images, owlProps, slideStyle, imageStyle, imageHeight, getImageSrc, carouselRef, containerIdRef, onInitialized, onChanged, owlClassName]);

    return (
        <div className={'user-phone-homepage-image-carousel'}>
            <div className={'user-phone-homepage-block-header component-title'}>
                <p>{component?.title ?? ''}</p>
            </div>
            <div
                ref={sliderRef}
                className={'user-phone-homepage-image-carousel__slider'}>
                {isScrollable &&
                    <button
                        disabled={!hasPrev}
                        className={'slider-icon-button prev'}
                        onClick={() => changeSlide(-1)}>
                        <Prev/>
                    </button>
                }
                {owlCarousel}
                {isScrollable &&
                    <button
                        disabled={!hasNext}
                        className={'slider-icon-button next'}
                        onClick={() => changeSlide(1)}>
                        <Next/>
                    </button>
                }
            </div>
        </div>
    );
}

export default UserPhoneHomepageImageCarousel;
